import classNames from 'classnames';
import React, { memo, useState, useRef, useCallback, useEffect } from 'react';
import { TableHeader, TableHeaderSort } from 'components/TableHeader/TableHeader';
import { getPatientPresence } from 'utils/patientPresence';
import { formatMinutesToHoursAndMinutes } from 'utils/time';
import style from './PatientTable.scss';
import { DeviceModeState } from '@redux/person/personTypes';
import { PatientTableColName, PatientTableProps, PATIENTS_SORT_STORAGE_KEY } from './PatientTableTypes';
import TableCell from './TableCell';

const TABLE_CELL_HEIGHT = 66;
export const DEFAULT_SORT_CONFIG = { key: null, direction: 'ascending' };

const Table: React.FC<PatientTableProps> = memo(({ patientTableData, onPressTableRow, onDeletePatientClick, onScrollBottom, onSortPatients, columnsConfig, hasPatientCreatePermission }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLTableElement>(null);

  const visibleTableHeaders = Object.values(columnsConfig).filter((item) => item.visible);
  const [sortConfig, setSortConfig] = useState<{ key: PatientTableColName | null; direction: TableHeaderSort }>(() => {
    const saved = localStorage.getItem(PATIENTS_SORT_STORAGE_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_SORT_CONFIG;
  });
  const toggleSort = (key) => {
    const newSortConfig: { key: PatientTableColName | null; direction: TableHeaderSort } = {
      key,
      direction: 'ascending'
    };
    setSortConfig((prev) => {
      newSortConfig.direction = prev.key === key ? (prev.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending';
      return newSortConfig;
    });
    localStorage.setItem(PATIENTS_SORT_STORAGE_KEY, JSON.stringify(newSortConfig));
    onSortPatients(key + '_' + newSortConfig.direction);
  };

  // Handle scrolling and pagination
  const handleScroll = useCallback(() => {
    const scrollLeft = scrollContainerRef.current?.scrollLeft || 0;
    setIsScrolled(scrollLeft > 0);
    const scrollHeight = scrollContainerRef.current?.scrollHeight || 0;
    const scrollTop = scrollContainerRef.current?.scrollTop || 0;
    const clientHeight = scrollContainerRef.current?.clientHeight || 0;

    if (scrollHeight - scrollTop - clientHeight <= TABLE_CELL_HEIGHT * 10 && onScrollBottom) {
      onScrollBottom();
    }
  }, []);

  useEffect(() => {
    const ref = scrollContainerRef.current;
    if (!ref) return;

    ref.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => ref.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <>
      <div className={style.tableWrapper} ref={scrollContainerRef} onScroll={handleScroll}>
        <table className={style.patientsTable}>
          <thead>
            <tr className={style.tableRow}>
              <TableHeader
                containerClassName={classNames(style.stickyFirstCol, isScrolled && style.scrolled)}
                onSort={() => toggleSort(PatientTableColName.NAME)}
                sort={sortConfig.key === PatientTableColName.NAME ? sortConfig.direction : 'none'}
              >
                Name
              </TableHeader>
              {visibleTableHeaders.map(({ key, title, canSort }) => (
                <TableHeader
                  key={key}
                  onSort={() => {
                    canSort && toggleSort(key);
                  }}
                  sort={sortConfig.key === key ? sortConfig.direction : 'none'}
                >
                  {title}
                </TableHeader>
              ))}
              <TableHeader containerClassName={style.detailsColumn}>{''}</TableHeader>
            </tr>
          </thead>
          <tbody>
            {patientTableData.map(
              ({
                personId,
                email,
                name,
                avatar,
                patientType,
                heartRate,
                heartRateStatus,
                heartRateLastReading,
                breathingRate,
                breathingRateStatus,
                breathingRateLastReading,
                sleepTimeInMinutes,
                sleepStatus,
                sleepTimeLastReading,
                sleepEfficiency,
                personPresence,
                personPresenceLastReading,
                deviceStatus,
                deviceMode,
                deviceNumber,
                nightSleepTimeInMinutes,
                nightSleepTimeInMinutesStatus: nightSleepTimeStatus,
                nightAwakeTimeInMinutes,
                nightAwakeTimeInMinutesStatus: nightAwakeTimeStatus,
                numberOfBedExits: bedExits,
                numberOfBedExitsStatus: bedExitsStatus,
                daySleepTimeInMinutes,
                daySleepTimeInMinutesStatus: daySleepTimeStatus
              }) => {
                const handleRowClick = () => {
                  onPressTableRow(personId);
                };

                const isLivingSpace = deviceMode === DeviceModeState.LIVING_SPACE;

                // Render a data cell for a column. Order follows the column config
                // (visibleTableHeaders) so each view controls its own column order and
                // stays aligned with its headers.
                const renderCell = (key: PatientTableColName) => {
                  switch (key) {
                    case PatientTableColName.PATIENT_TYPE:
                      return <TableCell key={key} colName={PatientTableColName.PATIENT_TYPE} value={patientType} />;
                    case PatientTableColName.HEART_RATE:
                      return <TableCell key={key} colName={PatientTableColName.HEART_RATE} value={isLivingSpace ? undefined : heartRate} modifiedDate={isLivingSpace ? undefined : heartRateLastReading} status={isLivingSpace ? undefined : heartRateStatus} />;
                    case PatientTableColName.BREATHING_RATE:
                      return <TableCell key={key} colName={PatientTableColName.BREATHING_RATE} value={isLivingSpace ? undefined : breathingRate} modifiedDate={isLivingSpace ? undefined : breathingRateLastReading} status={isLivingSpace ? undefined : breathingRateStatus} />;
                    case PatientTableColName.SLEEP_DURATION:
                      return (
                        <TableCell
                          key={key}
                          colName={PatientTableColName.SLEEP_DURATION}
                          value={isLivingSpace ? undefined : formatMinutesToHoursAndMinutes(sleepTimeInMinutes ?? 0, true)}
                          modifiedDate={isLivingSpace ? undefined : sleepTimeLastReading}
                          status={isLivingSpace ? undefined : sleepStatus}
                        />
                      );
                    case PatientTableColName.SLEEP_EFFICIENCY:
                      return <TableCell key={key} colName={PatientTableColName.SLEEP_EFFICIENCY} value={sleepEfficiency} />;
                    case PatientTableColName.DEVICE_MODE:
                      return <TableCell key={key} colName={PatientTableColName.DEVICE_MODE} value={deviceMode} />;
                    case PatientTableColName.PERSON_PRESENCE:
                      return <TableCell key={key} colName={PatientTableColName.PERSON_PRESENCE} value={getPatientPresence(personPresence, deviceStatus)} modifiedDate={personPresenceLastReading} />;
                    case PatientTableColName.DEVICE_NUMBER:
                      return <TableCell key={key} colName={PatientTableColName.DEVICE_NUMBER} value={deviceNumber} />;
                    case PatientTableColName.DEVICE_STATUS:
                      return <TableCell key={key} colName={PatientTableColName.DEVICE_STATUS} value={deviceStatus} />;
                    case PatientTableColName.NIGHT_SLEEP_TIME:
                      return <TableCell key={key} colName={PatientTableColName.NIGHT_SLEEP_TIME} value={formatMinutesToHoursAndMinutes(nightSleepTimeInMinutes ?? 0, true)} status={nightSleepTimeStatus} />;
                    case PatientTableColName.NIGHT_AWAKE_TIME:
                      return <TableCell key={key} colName={PatientTableColName.NIGHT_AWAKE_TIME} value={formatMinutesToHoursAndMinutes(nightAwakeTimeInMinutes ?? 0, true)} status={nightAwakeTimeStatus} />;
                    case PatientTableColName.BED_EXITS:
                      return <TableCell key={key} colName={PatientTableColName.BED_EXITS} value={bedExits} status={bedExitsStatus} />;
                    case PatientTableColName.DAY_SLEEP_TIME:
                      return <TableCell key={key} colName={PatientTableColName.DAY_SLEEP_TIME} value={formatMinutesToHoursAndMinutes(daySleepTimeInMinutes ?? 0, true)} status={daySleepTimeStatus} />;
                    default:
                      return null;
                  }
                };

              return (
                  <tr key={personId} onClick={handleRowClick} className={style.tableRow}>
                    <TableCell
                      containerClassName={classNames(style.stickyFirstCol, isScrolled && style.scrolled)}
                      colName={PatientTableColName.NAME}
                      value={name}
                      avatarUrl={avatar}
                      email={email}
                    />
                    {visibleTableHeaders.map(({ key }) => renderCell(key))}
                    <TableCell colName={PatientTableColName.DETAILS} value={personId} onDeletePatient={() => onDeletePatientClick?.(personId)} hasPatientCreatePermission={hasPatientCreatePermission} />
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>
    </>
  );
});

export default Table;
